import { ExprNode, ParseNode } from '@mode-switch/express';
import { Pipeline } from '../Pipeline';
export var ExprNodes;
(function (ExprNodes) {
    function make(catalog) {
        return (pipeline, node) => {
            switch (node.type) {
                case "NullLiteral":
                    return vNullLiteral(pipeline, node);
                case "BooleanLiteral":
                    return vBooleanLiteral(pipeline, node);
                case "NumberLiteral":
                    return vNumberLiteral(pipeline, node);
                case "HexLiteral":
                    return vNumberLiteral(pipeline, node);
                case "StringLiteral":
                    return vStringLiteral(pipeline, node);
                case "RegExpLiteral":
                    return vRegexpLiteral(pipeline, node);
                case "DateLiteral":
                    return vDateTimeLiteral(pipeline, node);
                case "Identifier":
                case "AccessIdentifier":
                case "AccessExpression":
                    return vAccess(pipeline, node);
                case "CaseExpression":
                    return vCase(pipeline, node);
                case "CaseWhen":
                    return vCaseWhen(pipeline, node);
                case "CaseElse":
                    return vCaseElse(pipeline, node);
                case "IfExpression":
                    return vIfExpression(pipeline, node);
                case "IfBase":
                    return vIfBase(pipeline, node);
                case "ElseIf":
                    return vElseIf(pipeline, node);
                case "IfElse":
                    return vIfElse(pipeline, node);
                case "CallExpression":
                    return vCall(pipeline, catalog, node);
                case "UnaryExpression":
                    return vUnary(pipeline, node);
                case "BinaryExpression":
                    return vBinary(pipeline, node);
                case "LogicalExpression":
                    return vLogical(pipeline, node);
                default:
                    return pipeline;
            }
        };
    }
    ExprNodes.make = make;
    function vNullLiteral(pipeline, node) {
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.NullLiteral.make());
    }
    ExprNodes.vNullLiteral = vNullLiteral;
    function vBooleanLiteral(pipeline, node) {
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.BooleanLiteral.make(node.value === true));
    }
    ExprNodes.vBooleanLiteral = vBooleanLiteral;
    function vNumberLiteral(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        if (type == null) {
            throw new Error(`Couldn't resolve data type of NumberLiteral at node ${ParseNode.id(node)}`);
        }
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.NumberLiteral.make(node.value, {
            type,
        }));
    }
    ExprNodes.vNumberLiteral = vNumberLiteral;
    function vStringLiteral(pipeline, node) {
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.StringLiteral.make(node.value));
    }
    ExprNodes.vStringLiteral = vStringLiteral;
    function vRegexpLiteral(pipeline, node) {
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.RegexpLiteral.make(node.value));
    }
    ExprNodes.vRegexpLiteral = vRegexpLiteral;
    function vDateTimeLiteral(pipeline, node) {
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.DateTimeLiteral.make(node.value * 1000));
    }
    ExprNodes.vDateTimeLiteral = vDateTimeLiteral;
    function vAccess(pipeline, node) {
        const path = Pipeline.Paths.get(pipeline, node);
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        if (path == null) {
            throw new Error(`Couldn't resolve path ${ParseNode.toString(node)}`);
        }
        if (type == null) {
            throw new Error(`Couldn't resolve data type of access at node ${ParseNode.id(node)}`);
        }
        if (Pipeline.Calcs.get(pipeline, node) != null) {
            return pipeline;
        }
        const attrIdent = ExprNode.Ident.make(path.name);
        if (path.view == null) {
            return Pipeline.ExprNodes.set(pipeline, node, ExprNode.Access.make([attrIdent], { type, role }));
        }
        else {
            const viewIdent = ExprNode.Ident.make(path.view);
            return Pipeline.ExprNodes.set(pipeline, node, ExprNode.Access.make([viewIdent, attrIdent], { type, role }));
        }
    }
    function vCase(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        if (type == null) {
            throw new Error(`Cannot find data type for ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of node ${node}`);
        }
        const valueNode = Pipeline.ExprNodes.get(pipeline, node.value);
        if (valueNode == null) {
            throw new Error(`Cannot determine value of case expression ${node.value}`);
        }
        switch (valueNode.type) {
            case "CALL":
            case "IDENT":
            case "ACCESS":
            case "NULL_LITERAL":
            case "BOOLEAN_LITERAL":
            case "NUMBER_LITERAL":
            case "STRING_LITERAL":
            case "DATE_TIME_LITERAL": {
                const condNodes = [];
                for (const caseNode of node.cases) {
                    const condNode = Pipeline.ExprNodes.get(pipeline, caseNode);
                    if (condNode == null) {
                        throw new Error(`Could not resolve case condition node ${condNode}`);
                    }
                    if (condNode.type === "CASE_COND") {
                        condNodes.push(condNode);
                    }
                    else {
                        throw new Error(`Wrong node type for case condition ${condNode}`);
                    }
                }
                if (node.else == null) {
                    return Pipeline.ExprNodes.set(pipeline, node, ExprNode.Case.make(valueNode, condNodes, stage, { type, role }));
                }
                else {
                    const elseNode = Pipeline.ExprNodes.get(pipeline, node.else);
                    if (elseNode == null) {
                        throw new Error(`Cannot determine value of case expression ${node.else}`);
                    }
                    return Pipeline.ExprNodes.set(pipeline, node, ExprNode.Case.makeWithElse(valueNode, condNodes, elseNode, stage, {
                        type,
                        role,
                    }));
                }
            }
            default:
                throw new Error(`Cannot evaluate case statement with value of type ${valueNode.type}`);
        }
    }
    function vCaseWhen(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        const valueNode = Pipeline.ExprNodes.get(pipeline, node.value);
        const resultNode = Pipeline.ExprNodes.get(pipeline, node.result);
        if (type == null) {
            throw new Error(`Cannot find data type for ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of node ${node}`);
        }
        if (valueNode == null) {
            throw new Error(`Cannot determine find expression node for case condition ${node}`);
        }
        if (resultNode == null) {
            throw new Error(`Cannot determine find expression node for case else result ${node}`);
        }
        switch (valueNode.type) {
            case "CALL":
            case "IDENT":
            case "ACCESS":
            case "NULL_LITERAL":
            case "BOOLEAN_LITERAL":
            case "NUMBER_LITERAL":
            case "STRING_LITERAL":
            case "DATE_TIME_LITERAL": {
                return Pipeline.ExprNodes.set(pipeline, node, ExprNode.CaseWhen.make(valueNode, resultNode, stage, { type, role }));
            }
            default:
                throw new Error(`Cannot evaluate case statement with value of type ${valueNode.type}`);
        }
    }
    function vCaseElse(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        const result = Pipeline.ExprNodes.get(pipeline, node.result);
        if (type == null) {
            throw new Error(`Cannot find data type for ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of node ${node}`);
        }
        if (result == null) {
            throw new Error(`Cannot determine find expression node for case else result ${node}`);
        }
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.CaseElse.make(result, stage, { type, role }));
    }
    function vIfExpression(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        if (type == null) {
            throw new Error(`Cannot find data type for ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of node ${node}`);
        }
        const baseNode = Pipeline.ExprNodes.get(pipeline, node.base);
        if (baseNode == null) {
            throw new Error(`Cannot determine value of if base expression ${node.base}`);
        }
        if (baseNode.type !== "IF_COND") {
            throw new Error(`Wrong node type for if/else base ${baseNode}`);
        }
        const condNodes = [];
        for (const elseIfNode of node.elses) {
            const condNode = Pipeline.ExprNodes.get(pipeline, elseIfNode);
            if (condNode == null) {
                throw new Error(`Could not resolve if/else condition ${condNode}`);
            }
            if (condNode.type === "IF_COND") {
                condNodes.push(condNode);
            }
            else {
                throw new Error(`Wrong node type for if/else condition ${condNode}`);
            }
        }
        if (node.else == null) {
            return Pipeline.ExprNodes.set(pipeline, node, ExprNode.IfElse.make(baseNode, condNodes, stage, { type, role }));
        }
        else {
            const elseNode = Pipeline.ExprNodes.get(pipeline, node.else);
            if (elseNode == null) {
                throw new Error(`Cannot determine value of case expression ${node.else}`);
            }
            return Pipeline.ExprNodes.set(pipeline, node, ExprNode.IfElse.makeWithElse(baseNode, condNodes, elseNode, stage, {
                type,
                role,
            }));
        }
    }
    function vIfBase(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        const cond = Pipeline.ExprNodes.get(pipeline, node.condition);
        const result = Pipeline.ExprNodes.get(pipeline, node.result);
        if (type == null) {
            throw new Error(`Cannot find data type for ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of node ${node}`);
        }
        if (cond == null) {
            throw new Error(`Cannot determine find expression node for if condition ${node}`);
        }
        if (result == null) {
            throw new Error(`Cannot determine find expression node for if condition result ${node}`);
        }
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.IfCond.make(cond, result, stage, { type, role }));
    }
    function vElseIf(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        const cond = Pipeline.ExprNodes.get(pipeline, node.condition);
        const result = Pipeline.ExprNodes.get(pipeline, node.result);
        if (type == null) {
            throw new Error(`Cannot find data type for ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of node ${node}`);
        }
        if (cond == null) {
            throw new Error(`Cannot determine find expression node for if condition ${node}`);
        }
        if (result == null) {
            throw new Error(`Cannot determine find expression node for if condition result ${node}`);
        }
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.IfCond.make(cond, result, stage, { type, role }));
    }
    function vIfElse(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        const result = Pipeline.ExprNodes.get(pipeline, node.result);
        if (type == null) {
            throw new Error(`Cannot find data type for ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of node ${node}`);
        }
        if (result == null) {
            throw new Error(`Cannot determine find expression node for case else result ${node}`);
        }
        return Pipeline.ExprNodes.set(pipeline, node, result);
    }
    function vCall(pipeline, catalog, node) {
        const call = Pipeline.ExprCalls.get(pipeline, node);
        const args = Pipeline.CallArgs.get(pipeline, node);
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        if (call == null) {
            throw new Error(`Cannot find call ${node.callee} at node ${node}`);
        }
        if (type == null) {
            throw new Error(`Cannot find data type for ${node.callee} at node ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of node ${node}`);
        }
        const callName = ExprNode.CallName.make(call.name);
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.Call.make(call, callName, args, stage, { type, role }));
    }
    function vUnary(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        const operand = Pipeline.ExprNodes.get(pipeline, node.operand);
        if (type == null) {
            throw new Error(`Cannot determine data type of unary expression ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of unary expression ${node}`);
        }
        if (operand == null) {
            throw new Error(`Cannot determine stage of unary operand ${node.operand}`);
        }
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.Unary.make(node.operator, operand, stage, { type, role }));
    }
    function vBinary(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        const left = Pipeline.ExprNodes.get(pipeline, node.left);
        const right = Pipeline.ExprNodes.get(pipeline, node.right);
        if (type == null) {
            throw new Error(`Cannot determine type of expr ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of expr ${node}`);
        }
        if (left == null) {
            throw new Error(`Cannot create expr from left operand ${node.left}`);
        }
        if (right == null) {
            throw new Error(`Cannot create expr from right operand ${node.left}`);
        }
        let operator = node.operator;
        if (right.type === "NULL_LITERAL") {
            switch (operator) {
                case '=':
                    operator = 'IS';
                    break;
                case '<>':
                    operator = 'IS NOT';
                    break;
            }
        }
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.Binary.make(operator, left, right, stage, { type, role }));
    }
    function vLogical(pipeline, node) {
        const type = Pipeline.DataTypes.get(pipeline, node);
        const role = Pipeline.DataRoles.get(pipeline, node);
        const stage = Pipeline.ExprStages.get(pipeline, node);
        const left = Pipeline.ExprNodes.get(pipeline, node.left);
        const right = Pipeline.ExprNodes.get(pipeline, node.right);
        if (type == null) {
            throw new Error(`Cannot determine type of expr ${node}`);
        }
        if (stage == null) {
            throw new Error(`Cannot determine stage of expr ${node}`);
        }
        if (left == null) {
            throw new Error(`Cannot create expr from left operand ${node.left}`);
        }
        if (right == null) {
            throw new Error(`Cannot create expr from right operand ${node.left}`);
        }
        return Pipeline.ExprNodes.set(pipeline, node, ExprNode.Logical.make(node.operator, left, right, stage, { type, role }));
    }
})(ExprNodes || (ExprNodes = {}));
